/**
 * Valeurs prédéfinies pour Azure
 * Référence complète des options valides pour différents types de propriétés
 */

// Toutes les régions Azure disponibles
export const AZURE_LOCATIONS = [
  // Americas
  'eastus',
  'eastus2',
  'centralus',
  'northcentralus',
  'southcentralus',
  'westcentralus',
  'westus',
  'westus2',
  'westus3',
  'canadacentral',
  'canadaeast',
  'brazilsouth',
  'brazilsoutheast',

  // Europe
  'northeurope',
  'westeurope',
  'uksouth',
  'ukwest',
  'francecentral',
  'francesouth',
  'germanywestcentral',
  'germanynorth',
  'norwayeast',
  'norwaywest',
  'switzerlandnorth',
  'switzerlandwest',
  'swedencentral',
  'polandcentral',

  // Asia Pacific
  'southeastasia',
  'eastasia',
  'australiaeast',
  'australiasoutheast',
  'australiacentral',
  'japaneast',
  'japanwest',
  'koreacentral',
  'koreasouth',
  'southindia',
  'centralindia',
  'westindia',

  // Middle East & Africa
  'uaenorth',
  'uaecentral',
  'southafricanorth',
  'southafricawest',
  'qatarcentral',
];

// Locations les plus populaires (pour les suggestions courtes)
export const AZURE_POPULAR_LOCATIONS = [
  'eastus',
  'westus2',
  'centralus',
  'northeurope',
  'westeurope',
  'francecentral',
  'uksouth',
  'southeastasia',
  'australiaeast',
  'canadacentral',
];

// VM Sizes - Organized by family
export const VM_SIZES = {
  // B-series (Burstable)
  'B_Series': [
    'Standard_B1ls',
    'Standard_B1s',
    'Standard_B1ms',
    'Standard_B2s',
    'Standard_B2ms',
    'Standard_B4ms',
    'Standard_B8ms',
    'Standard_B12ms',
    'Standard_B16ms',
    'Standard_B20ms',
  ],

  // D-series (General Purpose)
  'D_Series': [
    'Standard_D2s_v3',
    'Standard_D4s_v3',
    'Standard_D8s_v3',
    'Standard_D16s_v3',
    'Standard_D32s_v3',
    'Standard_D48s_v3',
    'Standard_D64s_v3',
    'Standard_D2s_v4',
    'Standard_D4s_v4',
    'Standard_D8s_v4',
    'Standard_D16s_v4',
    'Standard_D32s_v4',
    'Standard_D48s_v4',
    'Standard_D64s_v4',
    'Standard_D2s_v5',
    'Standard_D4s_v5',
    'Standard_D8s_v5',
    'Standard_D16s_v5',
    'Standard_D32s_v5',
    'Standard_D48s_v5',
    'Standard_D64s_v5',
    'Standard_D96s_v5',
  ],

  // E-series (Memory Optimized)
  'E_Series': [
    'Standard_E2s_v3',
    'Standard_E4s_v3',
    'Standard_E8s_v3',
    'Standard_E16s_v3',
    'Standard_E20s_v3',
    'Standard_E32s_v3',
    'Standard_E48s_v3',
    'Standard_E64s_v3',
    'Standard_E2s_v4',
    'Standard_E4s_v4',
    'Standard_E8s_v4',
    'Standard_E16s_v4',
    'Standard_E32s_v4',
    'Standard_E48s_v4',
    'Standard_E64s_v4',
    'Standard_E2s_v5',
    'Standard_E4s_v5',
    'Standard_E8s_v5',
    'Standard_E16s_v5',
    'Standard_E32s_v5',
    'Standard_E48s_v5',
    'Standard_E64s_v5',
    'Standard_E96s_v5',
  ],

  // F-series (Compute Optimized)
  'F_Series': [
    'Standard_F2s_v2',
    'Standard_F4s_v2',
    'Standard_F8s_v2',
    'Standard_F16s_v2',
    'Standard_F32s_v2',
    'Standard_F48s_v2',
    'Standard_F64s_v2',
    'Standard_F72s_v2',
  ],

  // M-series (Memory Optimized Large)
  'M_Series': [
    'Standard_M8ms',
    'Standard_M16ms',
    'Standard_M32ms',
    'Standard_M64ms',
    'Standard_M128ms',
  ],
};

// Flatten VM sizes for select dropdowns
export const VM_SIZES_ALL = Object.values(VM_SIZES).flat();

// Most popular VM sizes (for shorter lists)
export const VM_SIZES_POPULAR = [
  'Standard_B1s',
  'Standard_B2s',
  'Standard_B4ms',
  'Standard_D2s_v3',
  'Standard_D4s_v3',
  'Standard_D8s_v3',
  'Standard_D2s_v5',
  'Standard_D4s_v5',
  'Standard_E2s_v3',
  'Standard_E4s_v3',
  'Standard_F2s_v2',
  'Standard_F4s_v2',
];

// Storage Account Tiers
export const STORAGE_ACCOUNT_TIERS = [
  'Standard',
  'Premium',
];

// Storage Account Replication Types
export const STORAGE_REPLICATION_TYPES = [
  'LRS',   // Locally Redundant Storage
  'ZRS',   // Zone Redundant Storage
  'GRS',   // Geo-Redundant Storage
  'GZRS',  // Geo-Zone-Redundant Storage
  'RAGRS', // Read-Access Geo-Redundant Storage
  'RAGZRS', // Read-Access Geo-Zone-Redundant Storage
];

// Storage Account Access Tiers
export const STORAGE_ACCESS_TIERS = [
  'Hot',
  'Cool',
  'Archive',
];

// SQL Database SKUs
export const SQL_DATABASE_SKUS = [
  // DTU-based
  'Basic',
  'S0', 'S1', 'S2', 'S3', 'S4', 'S6', 'S7', 'S9', 'S12',
  'P1', 'P2', 'P4', 'P6', 'P11', 'P15',

  // vCore-based - General Purpose
  'GP_Gen5_2',
  'GP_Gen5_4',
  'GP_Gen5_8',
  'GP_Gen5_16',
  'GP_Gen5_24',
  'GP_Gen5_32',
  'GP_Gen5_40',
  'GP_Gen5_80',

  // vCore-based - Business Critical
  'BC_Gen5_2',
  'BC_Gen5_4',
  'BC_Gen5_8',
  'BC_Gen5_16',
  'BC_Gen5_24',
  'BC_Gen5_32',
  'BC_Gen5_40',
  'BC_Gen5_80',

  // Hyperscale
  'HS_Gen5_2',
  'HS_Gen5_4',
  'HS_Gen5_8',
  'HS_Gen5_16',
  'HS_Gen5_24',
];

// App Service / Function App SKUs
export const APP_SERVICE_SKUS = [
  // Free tier
  'F1',

  // Shared tier
  'D1',

  // Basic tier
  'B1', 'B2', 'B3',

  // Standard tier
  'S1', 'S2', 'S3',

  // Premium V2
  'P1v2', 'P2v2', 'P3v2',

  // Premium V3
  'P1v3', 'P2v3', 'P3v3',

  // Premium V3 Memory Optimized
  'P1mv3', 'P2mv3', 'P3mv3', 'P4mv3', 'P5mv3',

  // Isolated
  'I1', 'I2', 'I3',
  'I1v2', 'I2v2', 'I3v2',
];

// Runtime Stacks for App Service
export const APP_SERVICE_RUNTIME_STACKS = [
  // .NET
  'DOTNET|6.0',
  'DOTNET|7.0',
  'DOTNET|8.0',
  'DOTNETCORE|3.1',

  // Node.js
  'NODE|16-lts',
  'NODE|18-lts',
  'NODE|20-lts',

  // Python
  'PYTHON|3.8',
  'PYTHON|3.9',
  'PYTHON|3.10',
  'PYTHON|3.11',
  'PYTHON|3.12',

  // Java
  'JAVA|8',
  'JAVA|11',
  'JAVA|17',
  'JAVA|21',

  // PHP
  'PHP|8.0',
  'PHP|8.1',
  'PHP|8.2',
  'PHP|8.3',

  // Ruby
  'RUBY|3.0',
  'RUBY|3.1',
  'RUBY|3.2',
];

// Database Versions
export const SQL_DATABASE_VERSIONS = [
  '2.0',  // Latest
  '12.0', // SQL Server 2014
];

export const MYSQL_VERSIONS = [
  '5.7',
  '8.0',
];

export const POSTGRESQL_VERSIONS = [
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
];

// Kubernetes versions (AKS)
export const KUBERNETES_VERSIONS = [
  '1.26',
  '1.27',
  '1.28',
  '1.29',
  '1.30',
];

// Container Registry SKUs
export const CONTAINER_REGISTRY_SKUS = [
  'Basic',
  'Standard',
  'Premium',
];

// Key Vault SKUs
export const KEY_VAULT_SKUS = [
  'standard',
  'premium',
];

// Load Balancer SKUs
export const LOAD_BALANCER_SKUS = [
  'Basic',
  'Standard',
];

// Public IP SKUs
export const PUBLIC_IP_SKUS = [
  'Basic',
  'Standard',
];

// Public IP Allocation Methods
export const PUBLIC_IP_ALLOCATION_METHODS = [
  'Static',
  'Dynamic',
];

// Disk Types
export const DISK_TYPES = [
  'Standard_LRS',      // HDD
  'StandardSSD_LRS',   // Standard SSD
  'Premium_LRS',       // Premium SSD
  'PremiumV2_LRS',     // Premium SSD v2
  'UltraSSD_LRS',      // Ultra SSD
  'StandardSSD_ZRS',   // Zone-redundant Standard SSD
  'Premium_ZRS',       // Zone-redundant Premium SSD
];

// Disk Sizes (in GB) - Common sizes
export const DISK_SIZES = [
  32, 64, 128, 256, 512, 1024, 2048, 4096, 8192, 16384, 32767,
];

// OS Types
export const OS_TYPES = [
  'Linux',
  'Windows',
];

// Network Security Rule Protocols
export const NSG_PROTOCOLS = [
  'Tcp',
  'Udp',
  'Icmp',
  '*',
];

// Network Security Rule Access
export const NSG_ACCESS = [
  'Allow',
  'Deny',
];

// Network Security Rule Direction
export const NSG_DIRECTION = [
  'Inbound',
  'Outbound',
];

// Redis Cache SKUs
export const REDIS_CACHE_SKUS = [
  'Basic',
  'Standard',
  'Premium',
];

// Redis Cache Family
export const REDIS_CACHE_FAMILY = [
  'C', // Basic/Standard
  'P', // Premium
];

// Redis Cache Capacity (by family)
export const REDIS_CACHE_CAPACITY = {
  'C': [0, 1, 2, 3, 4, 5, 6],      // 250MB to 53GB
  'P': [1, 2, 3, 4, 5],            // 6GB to 120GB
};

// CosmosDB Kinds
export const COSMOSDB_KINDS = [
  'GlobalDocumentDB', // SQL API
  'MongoDB',
  'Cassandra',
  'Gremlin',
  'Table',
];

// CosmosDB Consistency Levels
export const COSMOSDB_CONSISTENCY_LEVELS = [
  'Strong',
  'BoundedStaleness',
  'Session',
  'ConsistentPrefix',
  'Eventual',
];

// Application Gateway SKUs
export const APP_GATEWAY_SKUS = [
  'Standard_Small',
  'Standard_Medium',
  'Standard_Large',
  'Standard_v2',
  'WAF_Medium',
  'WAF_Large',
  'WAF_v2',
];

// Application Gateway Tiers
export const APP_GATEWAY_TIERS = [
  'Standard',
  'Standard_v2',
  'WAF',
  'WAF_v2',
];

// Service Bus SKUs
export const SERVICE_BUS_SKUS = [
  'Basic',
  'Standard',
  'Premium',
];

// Event Hub SKUs
export const EVENT_HUB_SKUS = [
  'Basic',
  'Standard',
  'Premium',
];

// Backup Policy Types
export const BACKUP_POLICY_TYPES = [
  'V1',
  'V2',
];

// Common CIDR blocks for VNets
export const COMMON_VNET_CIDRS = [
  '10.0.0.0/16',
  '10.1.0.0/16',
  '10.10.0.0/16',
  '172.16.0.0/12',
  '192.168.0.0/16',
];

// Common CIDR blocks for Subnets
export const COMMON_SUBNET_CIDRS = [
  '10.0.1.0/24',
  '10.0.2.0/24',
  '10.0.3.0/24',
  '10.0.10.0/24',
  '10.0.20.0/24',
  '192.168.1.0/24',
  '192.168.2.0/24',
];

// Availability Zones
export const AVAILABILITY_ZONES = ['1', '2', '3'];

// Common Tags
export const COMMON_TAG_KEYS = [
  'environment',
  'project',
  'owner',
  'cost-center',
  'application',
  'managed-by',
  'created-by',
  'created-date',
];

export const ENVIRONMENT_VALUES = [
  'development',
  'staging',
  'production',
  'test',
  'qa',
];

// MariaDB Versions
export const MARIADB_VERSIONS = [
  '10.2',
  '10.3',
  '10.6',
  '10.11',
];

// Firewall SKUs
export const FIREWALL_SKUS = [
  'AZFW_VNet',
  'AZFW_Hub',
];

// Firewall Policy SKUs
export const FIREWALL_POLICY_SKUS = [
  'Standard',
  'Premium',
  'Basic',
];

// Application Gateway Capacity
export const APP_GATEWAY_CAPACITY = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 32, 125,
];

// VPN Gateway SKUs
export const VPN_GATEWAY_SKUS = [
  'Basic',
  'VpnGw1',
  'VpnGw2',
  'VpnGw3',
  'VpnGw4',
  'VpnGw5',
  'VpnGw1AZ',
  'VpnGw2AZ',
  'VpnGw3AZ',
  'VpnGw4AZ',
  'VpnGw5AZ',
];

// VPN Types
export const VPN_TYPES = [
  'RouteBased',
  'PolicyBased',
];

// Traffic Manager Routing Methods
export const TRAFFIC_MANAGER_ROUTING_METHODS = [
  'Performance',
  'Weighted',
  'Priority',
  'Geographic',
  'MultiValue',
  'Subnet',
];

// Traffic Manager Protocols
export const TRAFFIC_MANAGER_PROTOCOLS = [
  'HTTP',
  'HTTPS',
  'TCP',
];

// Cognitive Services Kinds
export const COGNITIVE_SERVICES_KINDS = [
  'AnomalyDetector',
  'ComputerVision',
  'ContentModerator',
  'CustomVision.Training',
  'CustomVision.Prediction',
  'Face',
  'FormRecognizer',
  'ImmersiveReader',
  'LUIS',
  'Personalizer',
  'QnAMaker',
  'SpeechServices',
  'TextAnalytics',
  'TextTranslation',
  'OpenAI',
];

// Cognitive Services SKUs
export const COGNITIVE_SERVICES_SKUS = [
  'F0',   // Free
  'S0',   // Standard
  'S1', 'S2', 'S3', 'S4',
];

// Machine Learning Workspace SKUs
export const MACHINE_LEARNING_SKUS = [
  'Basic',
  'Enterprise',
];

// Logic App SKUs
export const LOGIC_APP_SKUS = [
  'Standard',
  'WS1',
  'WS2',
  'WS3',
];

// API Management SKUs
export const API_MANAGEMENT_SKUS = [
  'Consumption',
  'Developer',
  'Basic',
  'Standard',
  'Premium',
];

// Databricks SKUs
export const DATABRICKS_SKUS = [
  'standard',
  'premium',
  'trial',
];

// Synapse SQL Pool SKUs
export const SYNAPSE_SQL_POOL_SKUS = [
  'DW100c',
  'DW200c',
  'DW300c',
  'DW400c',
  'DW500c',
  'DW1000c',
  'DW1500c',
  'DW2000c',
  'DW2500c',
  'DW3000c',
  'DW5000c',
  'DW6000c',
  'DW7500c',
  'DW10000c',
  'DW15000c',
  'DW30000c',
];

// Stream Analytics SKUs
export const STREAM_ANALYTICS_SKUS = [
  'Standard',
  'StandardV2',
];

// Stream Analytics Compatibility Levels
export const STREAM_ANALYTICS_COMPATIBILITY_LEVELS = [
  '1.0',
  '1.1',
  '1.2',
];

// Data Factory Integration Runtime Types
export const DATA_FACTORY_IR_TYPES = [
  'Managed',
  'SelfHosted',
];

// Container App Workload Profile Names
export const CONTAINER_APP_WORKLOAD_PROFILES = [
  'Consumption',
  'D4',
  'D8',
  'D16',
  'D32',
  'E4',
  'E8',
  'E16',
  'E32',
];

// Batch Pool Allocation Modes
export const BATCH_POOL_ALLOCATION_MODES = [
  'BatchService',
  'UserSubscription',
];

// Managed Disk Caching Options
export const DISK_CACHING_OPTIONS = [
  'None',
  'ReadOnly',
  'ReadWrite',
];

// SignalR Service SKUs
export const SIGNALR_SERVICE_SKUS = [
  'Free_F1',
  'Standard_S1',
  'Premium_P1',
];

// Search Service SKUs
export const SEARCH_SERVICE_SKUS = [
  'free',
  'basic',
  'standard',
  'standard2',
  'standard3',
  'storage_optimized_l1',
  'storage_optimized_l2',
];

// Media Services Account Storage Authentication
export const MEDIA_SERVICES_STORAGE_AUTH = [
  'System',
  'ManagedIdentity',
];

// Kusto (Data Explorer) SKUs
export const KUSTO_SKUS = [
  'Dev(No SLA)_Standard_D11_v2',
  'Dev(No SLA)_Standard_E2a_v4',
  'Standard_D11_v2',
  'Standard_D12_v2',
  'Standard_D13_v2',
  'Standard_D14_v2',
  'Standard_D16d_v5',
  'Standard_D32d_v4',
  'Standard_D32d_v5',
  'Standard_E2a_v4',
  'Standard_E4a_v4',
  'Standard_E8a_v4',
  'Standard_E16a_v4',
  'Standard_E64i_v3',
  'Standard_E80ids_v4',
  'Standard_L8s',
  'Standard_L16s',
  'Standard_L8s_v2',
  'Standard_L16s_v2',
];

// IoT Hub SKUs
export const IOT_HUB_SKUS = [
  'F1',   // Free
  'S1',   // Standard tier 1
  'S2',   // Standard tier 2
  'S3',   // Standard tier 3
  'B1',   // Basic tier 1
  'B2',   // Basic tier 2
  'B3',   // Basic tier 3
];

// DNS Record Types
export const DNS_RECORD_TYPES = [
  'A',
  'AAAA',
  'CAA',
  'CNAME',
  'MX',
  'NS',
  'PTR',
  'SOA',
  'SRV',
  'TXT',
];

// CDN SKUs
export const CDN_SKUS = [
  'Standard_Akamai',
  'Standard_Microsoft',
  'Standard_Verizon',
  'Premium_Verizon',
];

// Front Door SKUs
export const FRONTDOOR_SKUS = [
  'Classic_AzureFrontDoor',
  'Standard_AzureFrontDoor',
  'Premium_AzureFrontDoor',
];

// ExpressRoute Circuit SKUs
export const EXPRESS_ROUTE_SKUS = [
  'Standard_MeteredData',
  'Standard_UnlimitedData',
  'Premium_MeteredData',
  'Premium_UnlimitedData',
  'Local',
];

// ExpressRoute Service Provider Tiers
export const EXPRESS_ROUTE_TIERS = [
  'Standard',
  'Premium',
  'Local',
];

// ExpressRoute Families
export const EXPRESS_ROUTE_FAMILIES = [
  'MeteredData',
  'UnlimitedData',
];

// ExpressRoute Bandwidths (Mbps)
export const EXPRESS_ROUTE_BANDWIDTHS = [
  50, 100, 200, 500, 1000, 2000, 5000, 10000,
];

// Bastion Host SKUs
export const BASTION_HOST_SKUS = [
  'Basic',
  'Standard',
];

// Bot Service SKUs
export const BOT_SERVICE_SKUS = [
  'F0',   // Free
  'S1',   // Standard
];

// Automation Account SKUs
export const AUTOMATION_ACCOUNT_SKUS = [
  'Free',
  'Basic',
];

// Availability Set Platform Fault Domain Counts
export const AVAILABILITY_SET_FAULT_DOMAINS = [
  1, 2, 3,
];

// Availability Set Platform Update Domain Counts
export const AVAILABILITY_SET_UPDATE_DOMAINS = [
  1, 2, 3, 4, 5, 10, 20,
];

// Network Interface IP Configuration Versions
export const IP_CONFIGURATION_VERSIONS = [
  'IPv4',
  'IPv6',
];

// Managed Disk Create Options
export const DISK_CREATE_OPTIONS = [
  'Empty',
  'Attach',
  'FromImage',
  'Import',
  'Copy',
  'Restore',
  'Upload',
];

// Recovery Services Vault SKUs
export const RECOVERY_SERVICES_VAULT_SKUS = [
  'Standard',
  'RS0',  // GeoRedundant
];

// Backup Policy Frequency
export const BACKUP_POLICY_FREQUENCY = [
  'Daily',
  'Weekly',
];

// Event Grid Topic Types
export const EVENT_GRID_TOPIC_INPUT_SCHEMAS = [
  'EventGridSchema',
  'CustomEventSchema',
  'CloudEventSchemaV1_0',
];

// Application Insights Types
export const APPLICATION_INSIGHTS_TYPES = [
  'web',
  'other',
];

// Monitor Action Group Receiver Types
export const ACTION_GROUP_RECEIVER_TYPES = [
  'email',
  'sms',
  'webhook',
  'itsm',
  'azureapppush',
  'automationrunbook',
  'voicecall',
  'logicapp',
  'azurefunction',
];

// Service Fabric Cluster Reliability Levels
export const SERVICE_FABRIC_RELIABILITY_LEVELS = [
  'None',
  'Bronze',
  'Silver',
  'Gold',
  'Platinum',
];

// Service Fabric VM Image SKUs
export const SERVICE_FABRIC_VM_SIZES = [
  'Standard_D1_v2',
  'Standard_D2_v2',
  'Standard_D3_v2',
  'Standard_D4_v2',
  'Standard_D5_v2',
  'Standard_DS1_v2',
  'Standard_DS2_v2',
  'Standard_DS3_v2',
  'Standard_DS4_v2',
  'Standard_DS5_v2',
];

// Helper function to get formatted location display name
export const getLocationDisplayName = (location: string): string => {
  const displayNames: Record<string, string> = {
    'eastus': 'East US',
    'eastus2': 'East US 2',
    'westus': 'West US',
    'westus2': 'West US 2',
    'westus3': 'West US 3',
    'centralus': 'Central US',
    'northcentralus': 'North Central US',
    'southcentralus': 'South Central US',
    'westcentralus': 'West Central US',
    'northeurope': 'North Europe',
    'westeurope': 'West Europe',
    'uksouth': 'UK South',
    'ukwest': 'UK West',
    'francecentral': 'France Central',
    'francesouth': 'France South',
    'germanywestcentral': 'Germany West Central',
    'norwayeast': 'Norway East',
    'switzerlandnorth': 'Switzerland North',
    'southeastasia': 'Southeast Asia',
    'eastasia': 'East Asia',
    'australiaeast': 'Australia East',
    'australiasoutheast': 'Australia Southeast',
    'japaneast': 'Japan East',
    'japanwest': 'Japan West',
    'koreacentral': 'Korea Central',
    'southindia': 'South India',
    'centralindia': 'Central India',
    'canadacentral': 'Canada Central',
    'canadaeast': 'Canada East',
    'brazilsouth': 'Brazil South',
    'uaenorth': 'UAE North',
    'southafricanorth': 'South Africa North',
  };

  return displayNames[location] || location;
};
